/**
 * OutputCanvas Component
 * Right-panel with tabbed interface for displaying analysis results and generated apps
 */

import { useState } from 'react';
import {
  Paper,
  Tabs,
  Stack,
  Text,
  ScrollArea,
  Card,
  Group,
  Badge,
  Textarea,
  Alert,
  ActionIcon,
  Loader,
  Box
} from '@mantine/core';
import {
  IconBrowser,
  IconChartBar,
  IconCode,
  IconFileText,
  IconPlayerPlay,
  IconAlertCircle
} from '@tabler/icons-react';
import Chart from './Chart';
import CodeEditor from './CodeEditor';
import type { 
  AnalysisResult, 
  Generation, 
  Project, 
  ChartDataPoint, 
  Timecode 
} from '../../lib/types';
import { timeToSecs } from '../../lib/utils';

export interface OutputCanvasProps {
  analysisResults: AnalysisResult[];
  generatedApp: Generation | null;
  project: Project | null;
  onTimestampClick?: (time: number) => void;
}

export default function OutputCanvas({ 
  analysisResults, 
  generatedApp, 
  project: _project,
  onTimestampClick 
}: OutputCanvasProps) {
  const [activeTab, setActiveTab] = useState<string>('analysis');
  
  const renderAnalysisResult = (result: AnalysisResult) => {
    const isChartMode = result.mode === 'Chart';
    const isTimecodeMode = ['Key moments', 'A/V captions', 'Paragraph', 'Haiku'].includes(result.mode);
    const isTableMode = result.mode === 'Table';
    
    return (
      <Card key={result.id} shadow="sm" padding="md" mb="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Group>
            <IconChartBar size={20} />
            <Text fw={500}>{result.mode}</Text>
          </Group>
          <Badge size="sm" color="blue">
            {new Date(result.createdAt).toLocaleTimeString()}
          </Badge>
        </Group>
        
        {isChartMode && Array.isArray(result.resultData) && (
          <Box h={300}>
            <Chart 
              data={result.resultData as ChartDataPoint[]}
              yLabel={result.mode}
              jumpToTimecode={onTimestampClick}
            />
          </Box>
        )}
        
        {isTimecodeMode && Array.isArray(result.resultData) && (
          <ScrollArea h={200}>
            <Stack gap="xs">
              {(result.resultData as Timecode[]).map((item, index) => (
                <Group 
                  key={index} 
                  justify="space-between" 
                  p="sm" 
                  style={{ 
                    border: '1px solid var(--mantine-color-gray-3)',
                    borderRadius: '4px',
                    cursor: onTimestampClick ? 'pointer' : 'default'
                  }}
                  onClick={() => onTimestampClick && onTimestampClick(timeToSecs(item.time))}
                >
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Text size="sm">{item.text}</Text>
                    {item.objects && (
                      <Group gap={4}>
                        {item.objects.map((obj, i) => (
                          <Badge key={i} size="xs" variant="outline">
                            {obj}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Stack>
                  <Group gap="xs" align="center">
                    <Text size="xs" color="dimmed">{item.time}</Text>
                    {onTimestampClick && (
                      <ActionIcon size="sm" variant="subtle">
                        <IconPlayerPlay size={12} />
                      </ActionIcon>
                    )}
                  </Group>
                </Group>
              ))}
            </Stack>
          </ScrollArea>
        )}
        
        {isTableMode && Array.isArray(result.resultData) && (
          <ScrollArea>
            <Stack gap="xs">
              {(result.resultData as Timecode[]).map((item, index) => (
                <Card 
                  key={index} 
                  shadow="xs" 
                  padding="sm"
                  style={{ cursor: onTimestampClick ? 'pointer' : 'default' }}
                  onClick={() => onTimestampClick && onTimestampClick(timeToSecs(item.time))}
                >
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>{item.text}</Text>
                    <Text size="xs" color="dimmed">{item.time}</Text>
                  </Group>
                  {item.objects && (
                    <Group gap={4} mt="xs">
                      {item.objects.map((obj, i) => (
                        <Text key={i} size="xs">
                          {obj}
                        </Text>
                      ))}
                    </Group>
                  )}
                </Card>
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Card>
    );
  };

  return (
    <Paper shadow="sm" p="md" h="100%" style={{ minHeight: '600px' }}>
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'analysis')}>
        <Tabs.List>
          <Tabs.Tab 
            value="analysis" 
            leftSection={<IconChartBar size={14} />}
          >
            Analysis
          </Tabs.Tab>
          <Tabs.Tab 
            value="render" 
            leftSection={<IconBrowser size={14} />}
          >
            Render
          </Tabs.Tab>
          <Tabs.Tab 
            value="code" 
            leftSection={<IconCode size={14} />}
          >
            Code
          </Tabs.Tab>
          <Tabs.Tab 
            value="spec" 
            leftSection={<IconFileText size={14} />}
          >
            Spec
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="analysis" pt="md">
          <ScrollArea h="calc(100vh - 200px)">
            {analysisResults.length > 0 ? (
              <Stack gap="md">
                {analysisResults.map(renderAnalysisResult)}
              </Stack>
            ) : (
              <Stack align="center" justify="center" h={300}>
                <IconChartBar size={48} color="var(--mantine-color-dimmed)" />
                <Text color="dimmed" size="lg" fw={500}>
                  No Analysis Results Yet
                </Text>
                <Text color="dimmed" size="sm" ta="center">
                  Select an analysis mode from the toolbar and run it to see insights from your video.
                </Text>
              </Stack>
            )}
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="render" pt="md">
          {generatedApp && generatedApp.status === 'completed' ? (
            <Box h="calc(100vh - 200px)">
              <iframe 
                srcDoc={generatedApp.generatedCode}
                title="Generated Application"
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: '8px'
                }}
                sandbox="allow-scripts allow-same-origin"
              />
            </Box>
          ) : generatedApp && generatedApp.status === 'pending' ? (
            <Stack align="center" justify="center" h={300}>
              <Loader size="lg" />
              <Text color="dimmed">Generating your learning app...</Text>
            </Stack>
          ) : generatedApp && generatedApp.status === 'failed' ? (
            <Stack align="center" justify="center" h={300}>
              <Alert 
                icon={<IconAlertCircle size={16} />} 
                color="red" 
                title="Generation Failed"
              >
                There was an error generating your learning app. Please try again.
              </Alert>
            </Stack>
          ) : (
            <Stack align="center" justify="center" h={300}>
              <IconBrowser size={48} color="var(--mantine-color-dimmed)" />
              <Text color="dimmed" size="lg" fw={500}>
                No Generated App
              </Text>
              <Text color="dimmed" size="sm" ta="center">
                Use the "Generate Learning App" button to create an interactive application from your video.
              </Text>
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="code" pt="md">
          {generatedApp ? (
            <CodeEditor
              value={generatedApp.generatedCode || ''}
              language="html"
              readOnly={true}
              height="calc(100vh - 200px)"
            />
          ) : (
            <Stack align="center" justify="center" h={300}>
              <IconCode size={48} color="var(--mantine-color-dimmed)" />
              <Text color="dimmed" size="lg" fw={500}>
                No Code Available
              </Text>
              <Text color="dimmed" size="sm" ta="center">
                Generate a learning app to view the source code.
              </Text>
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="spec" pt="md">
          {generatedApp ? (
            <Textarea
              value={generatedApp.generatedSpec || ''}
              readOnly
              placeholder="Generated specification will appear here..."
              autosize
              minRows={15}
              maxRows={25}
              styles={{
                input: {
                  fontFamily: 'Monaco, Menlo, monospace',
                  fontSize: '12px'
                }
              }}
            />
          ) : (
            <Stack align="center" justify="center" h={300}>
              <IconFileText size={48} color="var(--mantine-color-dimmed)" />
              <Text color="dimmed" size="lg" fw={500}>
                No Specification Available
              </Text>
              <Text color="dimmed" size="sm" ta="center">
                Generate a learning app to view the technical specification.
              </Text>
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
    </Paper>
  );
}